import {
  css, Global,
} from '@emotion/react';
import {
  useCallback, useEffect, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { SQUARE_ICON_BUTTON_HOVER_SHADOW_SPREAD } from '~/_shared/baseComponents/buttons';
import { CONFIG } from '~/_shared/constants/config';
import type { Theme } from '~/_shared/themes/theme.model';
import { isSafari } from '~/_shared/utils/deviceDetect/deviceDetect.helpers';
import { useHookWithRefCallback } from '~/_shared/utils/hooks/useHookWithRefCallback';
import { useIsComponentMountedRef } from '~/_shared/utils/hooks/useIsComponentMountedRef';
import {
  MAPS_ATTRIBUTION_BAR_BOTTOM_OFFSET_PX, RIGHT_SIDE_RIGHT_OFFSET_PX,
} from '~/map/mapToolControls/mapOffsets.constants';
import { useZoomControlsVisible } from '~/map/mapToolControls/menuItems/zoomControls/useShowZoomControls.hook';
import { ZOOM_BUTTON_SIZE } from '~/map/mapToolControls/menuItems/zoomControls/zoomControls.component';
import { MOBILE_TOOL_PANEL_OPEN_BOTTOM_SAFE_OFFSET_PX } from '~/map/mapToolControls/mobileToolPanelOpenOffsets.constants';
import { useIsMobileToolPanelOpen } from '~/map/mapToolControls/useIsMobileToolPanelOpen.hook';
import { useSlidingAnimationMsDuration } from '~/store/frontendState/graphicSettings/graphicSettings.selectors';
import { frontedStateProcessingGoogleScriptInitialized } from '~/store/frontendState/processing/processing.actionCreators';
import { OverlayLoaderComponent } from '../overlay/overlayLoader.component';
import {
  PEGMAN_NEIGHBOR_GAP_PX, PEGMAN_SIZE_PX,
} from './googleMap.constants';
import {
  type GoogleMapInstance, useGetGoogleMapInstance,
} from './useGetGoogleMapInstance.hook';

const PEGMAN_RIGHT_OFFSET_NEXT_TO_COLUMN = RIGHT_SIDE_RIGHT_OFFSET_PX + ZOOM_BUTTON_SIZE + PEGMAN_NEIGHBOR_GAP_PX;
const PEGMAN_RIGHT_OFFSET_ALONE = RIGHT_SIDE_RIGHT_OFFSET_PX;

export const MAIN_GOOGLE_MAP_ID = 'MAIN_GOOGLE_MAP_ID';
export const DEFAULT_GOOGLE_MAPS_SETTINGS = {
  center: { lat: 40, lng: 0 },
  zoom: 3,
};

const wrapperStyle = (theme: Theme) => css({
  height: '100%',
  width: '100%',
  //the following styles are for the street view pegman, this is undocumented in google maps api, but the only way
  '& .gm-svpc': {
    backgroundColor: `${theme.backgroundColors.quinary} !important`,
    borderRadius: '8px  !important',
    borderWidth: '0 !important',
    boxShadow: `0 0 0 1px ${theme.borderColors.primary} !important`,
    height: `${PEGMAN_SIZE_PX}px !important`,
    width: `${PEGMAN_SIZE_PX}px !important`,
  },
  '& .gm-svpc:hover': {
    backgroundColor: `${theme.iconColors.backgroundQuaternaryHover} !important`,
    border: `2px solid ${theme.borderColors.activeItem} !important`,
    boxShadow: `inset ${theme.backgroundColors.secondary} 0px 0px 6px ${SQUARE_ICON_BUTTON_HOVER_SHADOW_SPREAD}px !important`,
    boxSizing: 'border-box',
  },
});

// The street view control's slot is set via ControlPosition (RIGHT_BOTTOM) in the map options. Google only
// offers fixed slots, so translate its positioned parent left (to clear the zoom column) and down (to line
// up with the zoom bottom). The transform moves the whole control reliably and makes the parent the button's
// containing block, so the button's right-edge anchor resolves against it. Kept apart from wrapperStyle (visual only).
const pegmanColumnPositionStyle = (hasNeighbor: boolean, isMobileToolPanelOpen: boolean, transitionDurationMs: number) => css({
  '& :has(> .gm-svpc)': {
    margin: '0 !important',
    right: `${(hasNeighbor ? PEGMAN_RIGHT_OFFSET_NEXT_TO_COLUMN : PEGMAN_RIGHT_OFFSET_ALONE)}px !important`,
    bottom: `${isMobileToolPanelOpen ? MOBILE_TOOL_PANEL_OPEN_BOTTOM_SAFE_OFFSET_PX : MAPS_ATTRIBUTION_BAR_BOTTOM_OFFSET_PX}px !important`,
    transition: `right ${transitionDurationMs}ms ease-in-out, bottom ${transitionDurationMs}ms ease-in-out !important`,
  },
  '& .gm-svpc': {
    left: 'auto !important',
    right: '0 !important',
    top: 'auto !important',
    bottom: '0 !important',
  },
});

type GoogleMaComponentProps = Readonly<{
  onMapInitialized: (map: GoogleMapInstance) => void;
  id?: string;
  className?: string;
}>;

export const GoogleMapComponent = ({ onMapInitialized, id = MAIN_GOOGLE_MAP_ID, className }: GoogleMaComponentProps) => {
  const dispatch = useDispatch();
  const zoomControlsVisible = useZoomControlsVisible();
  const isMobileToolPanelOpen = useIsMobileToolPanelOpen();
  const transitionDurationMs = useSlidingAnimationMsDuration();

  const [isProjectionInitialized, setIsProjectionInitialized] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const isMountedRef = useIsComponentMountedRef();
  const [mapContainer, setMapContainerRef] = useHookWithRefCallback<HTMLDivElement>();

  const instance = useGetGoogleMapInstance({
    id,
    useIdAsMapId: true,
  });

  const onProjectionChange = useCallback(() => {
    if (isMountedRef && instance?.container?.isConnected) {
      setIsProjectionInitialized(true);
    }
  }, [instance?.container?.isConnected, isMountedRef]);

  useEffect(() => {
    if (!isMountedRef || isInitialized || !mapContainer || !instance?.instance) {
      return;
    }

    google.maps.event.addListenerOnce(instance.instance, 'idle', () => {
      if (instance?.instance && !instance.instance.getProjection()) {
        google.maps.event.addListenerOnce(instance.instance, 'projection_changed', () => {
          if (isMountedRef) {
            onProjectionChange();
          }
        });
      }
      else {
        if (isMountedRef) {
          onProjectionChange();
        }
      }
    });
    mapContainer.appendChild(instance.container);
    google.maps.event.trigger(instance.instance, 'idle');

    dispatch(frontedStateProcessingGoogleScriptInitialized());
    setIsInitialized(true);

    return () => {
      setIsInitialized(false);
      setIsProjectionInitialized(false);
      mapContainer.removeChild(instance.container);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapContainer, onMapInitialized, dispatch, isMountedRef, instance]);

  useEffect(() => {
    if (isMountedRef && isProjectionInitialized && isInitialized && instance?.instance) {
      onMapInitialized(instance);
    }
  }, [onMapInitialized, isInitialized, isProjectionInitialized, instance, isMountedRef]);

  useEffect(() => {
    // https://maptive.atlassian.net/browse/MD-4785
    // workaround for performance problems with safari when map is active element
    if (!isSafari() || !mapContainer) {
      return;
    }

    const observer = new ResizeObserver(() => {
      if (mapContainer.isConnected) {
        mapContainer.querySelector<HTMLDivElement>('div[aria-label="Map"]')?.blur();
      }
    });

    observer.observe(mapContainer);
    return () => {
      // Cleanup the observer by unobserving all elements
      observer.disconnect();
    };
  }, [mapContainer]);

  return (
    <div
      ref={setMapContainerRef}
      css={[wrapperStyle, pegmanColumnPositionStyle(zoomControlsVisible, isMobileToolPanelOpen, transitionDurationMs)]}
      className={className}
    >
      <Global
        styles={{
          '.gm-style iframe + div': {
            border: 'none !important',
          },
        }}
      />
      {!isInitialized && !CONFIG.HIDE_GOOGLE_MAP && (
        <OverlayLoaderComponent />
      )}
    </div>
  );
};
